import { LitElement, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { ConvoStateController } from '../services/convo-state.js';
import { router, navigateTo } from '../services/router.js';
import '../components/convo/cp-convo-session-list.js';
import '../components/convo/cp-convo-session-detail.js';
import '../components/convo/cp-convo-session-form.js';
import type { CpConvoSessionForm } from '../components/convo/cp-convo-session-form.js';

@customElement('cp-page-conversations')
export class CpPageConversations extends LitElement {
  createRenderRoot() { return this; }

  private _convoState = new ConvoStateController(this);

  @query('cp-convo-session-form') private _sessionForm!: CpConvoSessionForm;

  connectedCallback(): void {
    super.connectedCallback();
    router.addEventListener('route-changed', this._onRouteChanged);
    this._loadForCurrentRoute();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    router.removeEventListener('route-changed', this._onRouteChanged);
  }

  private _onRouteChanged = (): void => {
    this._loadForCurrentRoute();
    this.requestUpdate();
  };

  private _loadForCurrentRoute(): void {
    const params = router.getParams();
    if (params.id) {
      void this._convoState.loadOne(Number(params.id));
    } else {
      void this._convoState.loadAll();
    }
  }

  private _handleSessionSelected(e: CustomEvent<{ sessionId: number }>): void {
    navigateTo(`/conversations/${e.detail.sessionId}`);
  }

  private _handleCreateSession(): void {
    this._sessionForm.open();
  }

  private _handleSessionCreated(): void {
    void this._convoState.loadAll();
  }

  private _handleSessionUpdated(): void {
    const params = router.getParams();
    if (params.id) {
      void this._convoState.loadOne(Number(params.id));
    }
  }

  private _handleSessionDeleted(): void {
    navigateTo('/conversations');
    void this._convoState.loadAll();
  }

  render() {
    const params = router.getParams();
    if (params.id) {
      return this._renderDetailView(Number(params.id));
    }
    return this._renderListView();
  }

  private _renderListView() {
    const { loadingState, sessions, error } = this._convoState;

    return html`
      <div class="cp-page">
        <div class="cp-page-conversations__header">
          <div class="cp-page-conversations__header-row">
            <h1 class="cp-page__title">Conversation Coach</h1>
            <button class="cp-page-conversations__add-btn" @click=${this._handleCreateSession}>
              + Neue Session
            </button>
          </div>
          <p class="cp-page-conversations__subtitle">Gespraeche strategisch vorbereiten und analysieren</p>
        </div>

        ${loadingState === 'loading' ? this._renderLoading() : ''}
        ${loadingState === 'error' ? this._renderError(error) : ''}
        ${loadingState === 'loaded' || loadingState === 'idle' ? html`
          <cp-convo-session-list
            .sessions=${sessions}
            @session-selected=${this._handleSessionSelected}
          ></cp-convo-session-list>
        ` : ''}
      </div>

      <cp-convo-session-form @session-created=${this._handleSessionCreated}></cp-convo-session-form>
    `;
  }

  private _renderDetailView(sessionId: number) {
    const { activeSession, error } = this._convoState;

    if (error) {
      return html`<div class="cp-page">${this._renderError(error)}</div>`;
    }

    if (!activeSession || activeSession.id !== sessionId) {
      return html`
        <div class="cp-page">
          <p class="cp-page__placeholder">Lade Session...</p>
        </div>
      `;
    }

    return html`
      <div class="cp-page">
        <cp-convo-session-detail
          .session=${activeSession}
          @session-updated=${this._handleSessionUpdated}
          @session-deleted=${this._handleSessionDeleted}
        ></cp-convo-session-detail>
      </div>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-conversations__grid--skeleton">
        ${[1, 2, 3, 4].map(() => html`
          <div class="cp-page-conversations__skeleton">
            <div class="cp-page-conversations__skeleton-line cp-page-conversations__skeleton-line--badge"></div>
            <div class="cp-page-conversations__skeleton-line cp-page-conversations__skeleton-line--title"></div>
            <div class="cp-page-conversations__skeleton-line cp-page-conversations__skeleton-line--desc"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderError(message: string | null) {
    return html`
      <div class="cp-page-conversations__error">
        <p>Fehler beim Laden: ${message ?? 'Unbekannter Fehler'}</p>
        <button class="cp-page-conversations__retry-btn" @click=${() => this._loadForCurrentRoute()}>
          Erneut versuchen
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-conversations': CpPageConversations;
  }
}
